import type { LobbyGame, LobbySectionResponse } from '../api/getLobby';
import { filterUpcomingByDateGames, type UpcomingWindow } from './lobbyUpcomingByDate';

export interface LobbySectionSourceDef {
  sport: string;
  game_type: string;
  tags: string[];
  display_order: number;
}

export interface LobbySectionRuleDef {
  rule_type: string;
  payload: Record<string, unknown>;
  display_order: number;
}

export interface LobbySectionLayoutDef {
  id: string;
  kind: string;
  payload: Record<string, unknown>;
  max_games?: number;
  sources?: LobbySectionSourceDef[];
  rules?: LobbySectionRuleDef[];
  manual_pickem_ids?: string[];
}

function normalizeSport(value: string | null | undefined): string {
  if (!value) return 'all';
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_-]/g, '');
}

function normalizeGameType(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase().replace(/\s+/g, '_').replace(/-+/g, '_');
}

function normalizeTag(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-_]/g, '');
}

function pickLobbyTags(game: LobbyGame): string[] {
  return (game.lobby_tags ?? []).map(t => normalizeTag(String(t))).filter(Boolean);
}

function tagsMatch(pickemTags: string[], required: string[]): boolean {
  if (required.length === 0) return true;
  const set = new Set(pickemTags);
  return required.every(t => set.has(normalizeTag(t)));
}

function matchesSource(game: LobbyGame, src: LobbySectionSourceDef): boolean {
  const gSport = normalizeSport(game.sport);
  const sSport = normalizeSport(src.sport);
  if (sSport !== 'all' && sSport !== gSport) return false;
  const sType = normalizeGameType(src.game_type);
  const gType = normalizeGameType(game.game_type);
  if (sType !== 'all' && sType !== gType) return false;
  return tagsMatch(pickLobbyTags(game), (src.tags ?? []).map(normalizeTag).filter(Boolean));
}

function isManualPopulationMode(payload: Record<string, unknown> | undefined): boolean {
  return payload?.population_mode === 'manual';
}

function parseUpcomingWindow(raw: string | undefined): UpcomingWindow {
  if (raw === '14d' || raw === '30d') return raw;
  return '7d';
}

function applyUpcomingByDateRule(games: LobbyGame[], rule: LobbySectionRuleDef): LobbyGame[] {
  const window = parseUpcomingWindow(String((rule.payload as { window?: string })?.window ?? '7d'));
  return filterUpcomingByDateGames(games, window);
}

/** Client-side section fill for rules that only need contest metadata already on LobbyGame. */
export function resolveSectionGamesClient(
  section: LobbySectionLayoutDef,
  pool: LobbyGame[]
): LobbyGame[] {
  if (section.kind !== 'games_row') return [];

  const maxGames = Math.min(100, Math.max(1, section.max_games ?? 20));
  const isManualMode = isManualPopulationMode(section.payload);
  const sources = section.sources ?? [];
  const gameById = new Map(pool.map(g => [g.pickem_id, g]));

  const candidates =
    sources.length === 0 ? pool : pool.filter(g => sources.some(src => matchesSource(g, src)));

  const ordered: LobbyGame[] = [];

  for (const id of section.manual_pickem_ids ?? []) {
    if (ordered.length >= maxGames) break;
    const game = gameById.get(id);
    if (!game) continue;
    if (!isManualMode && !candidates.some(c => c.pickem_id === id)) continue;
    ordered.push(game);
  }

  if (isManualMode) {
    return ordered.slice(0, maxGames);
  }

  const rules = [...(section.rules ?? [])].sort((a, b) => a.display_order - b.display_order);
  const candidateSet = new Set(candidates.map(c => c.pickem_id));

  const appendUnique = (games: LobbyGame[]) => {
    for (const game of games) {
      if (ordered.length >= maxGames) return;
      if (!candidateSet.has(game.pickem_id)) continue;
      if (ordered.some(g => g.pickem_id === game.pickem_id)) continue;
      ordered.push(game);
    }
  };

  for (const rule of rules) {
    if (ordered.length >= maxGames) break;
    const remaining = candidates.filter(c => !ordered.some(g => g.pickem_id === c.pickem_id));

    if (rule.rule_type === 'upcoming_by_date') {
      appendUnique(applyUpcomingByDateRule(remaining, rule));
    }
  }

  if (rules.length === 0) {
    appendUnique([...candidates].sort((a, b) => a.display_order - b.display_order));
  }

  return ordered.slice(0, maxGames);
}

export function sectionUsesClientResolvableRule(section: LobbySectionResponse): boolean {
  return (section.rules ?? []).some(rule => rule.rule_type === 'upcoming_by_date');
}
